import * as React from 'react';
import classNames from 'classnames';
import { store } from 'Models/Store';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';

export interface INewAlertModalProps {
	/** The text to be placed in the cancel button */
	cancelText?: React.ReactNode;
	/** Class name on the modal */
	className?: string;
	/** Class name on the header div */
	headerClassName?: string;
	/** Class name on the message div */
	messageClassName?: string;
	/** Class name for the modal overlay */
	overlayClassName?: string;
	/** Boolean indicating if the overlay should close the modal. Defaults to true. */
	shouldCloseOnOverlayClick?: boolean;
	/** Boolean indicating if pressing the esc key should close the modal */
	shouldCloseOnEsc?: boolean;
	buttonColour?: Colors;
	/** A label for the modal for screen readers */
	label?: string;
}

/**
 * Displays an alert on the screen
 * @param title A title for the modal
 * @param message The message content for the modal
 * @param options Extra options for the modal
 * @returns A promise that is resolved when the modal is closed
 */
export function newAlertModal(title: React.ReactNode, message: React.ReactNode, options: INewAlertModalProps = {}) {
	return new Promise<void>(resolve => {
		const onClose = () => {
			store.modal.hide();
			resolve();
		};

		const cancelText = options.cancelText ?? 'Cancel';
		const { shouldCloseOnEsc = true, shouldCloseOnOverlayClick = true } = options;

		const alertDom = (
			<>
				<div key="header" className={classNames('new-modal__header', options.headerClassName)}>
					<h2 key="header-item" className="new-modal__title">
						{title}
					</h2>
				</div>
				<div key="message" className={classNames('new-modal__message', options.messageClassName)}>
					{message}
				</div>
				<div className="new-modal__actions">
					<Button
						key="action-cancel"
						className="new-modal--cancel"
						onClick={onClose}
						display={Display.Solid}
						colors={Colors.Secondary}
						newStyles
					>
						{cancelText}
					</Button>
				</div>
			</>
		);

		const label = options.label ?? (typeof title === 'string' ? title : 'Modal');

		store.modal.show(label, alertDom, {
			className: classNames('new-alert-modal', options.className),
			overlayClassName: options.overlayClassName,
			onRequestClose: onClose,
			shouldCloseOnEsc: options.shouldCloseOnEsc,
			shouldCloseOnOverlayClick: options.shouldCloseOnOverlayClick,
			newModal: true,
		});
	});
}
